/**
 * Closed filter-AST: validation against the dataset schema + a deterministic
 * matcher. This is the single source of truth for "does this row match?" — the
 * query engine (highlight/filter) and any style-filter fold both run through
 * it, so an NL answer's match set is identical to what the rendered filter
 * would show. Attribute-leaf semantics mirror `@/lib/map/expression`'s
 * `clauseExpression` (string-coalesced equality, numeric coercion for
 * comparisons, substring `contains`) so NL and click produce the same state.
 *
 * Out-of-schema fields HARD-FAIL into a validation error (M5.md: never
 * hallucinated) — the caller turns that into an NLResponse `fallbacks` note.
 */

import type { FilterLeaf, FilterNode } from "@/lib/contracts/nl";
import type { Point } from "@/lib/map/pointset";
import { haversineMeters } from "@/lib/geo/geoMath";

const GEO_OPS = new Set(["geo_in_region", "geo_within_km"]);

function isLeaf(node: FilterNode): node is FilterLeaf {
  return "op" in node && "field" in node;
}

/** Collect every attribute (non-geo) field referenced by the tree. */
function referencedFields(node: FilterNode, out: string[]): void {
  if (isLeaf(node)) {
    if (!GEO_OPS.has(node.op)) out.push(node.field);
    return;
  }
  if ("all" in node) node.all.forEach((n) => referencedFields(n, out));
  else if ("any" in node) node.any.forEach((n) => referencedFields(n, out));
  else if ("not" in node) referencedFields(node.not, out);
}

export interface FilterValidation {
  ok: boolean;
  /** Referenced fields absent from the schema (the hard-fail set). */
  unknownFields: string[];
}

/**
 * Validate a filter tree against the known column names. Returns the set of
 * out-of-schema fields; `ok` is true only when that set is empty. Comparison is
 * exact (case-sensitive) — fuzzy resolution happens upstream in the resolver,
 * so by the time a plan reaches here every field should be a real column.
 */
export function validateFilter(node: FilterNode, columnNames: string[]): FilterValidation {
  const known = new Set(columnNames);
  const refs: string[] = [];
  referencedFields(node, refs);
  const unknownFields = [...new Set(refs.filter((f) => !known.has(f)))];
  return { ok: unknownFields.length === 0, unknownFields };
}

const toNum = (v: string | number): number => (typeof v === "number" ? v : Number(v));

/**
 * Great-circle distance in kilometres (haversine). Derived from the shared
 * geo-math foundation ({@link haversineMeters}) — same authalic-sphere radius,
 * expressed in km. `haversineMeters` takes [lng,lat]; this takes lat/lng args.
 */
export function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  return haversineMeters([aLng, aLat], [bLng, bLat]) / 1000;
}

/** A bbox region: [minLng, minLat, maxLng, maxLat]. */
interface RegionValue {
  bbox: [number, number, number, number];
}
/** A within-radius value: an anchor point + radius in km. */
interface RadiusValue {
  lat: number;
  lng: number;
  km: number;
}

function matchLeaf(point: Point, leaf: FilterLeaf): boolean {
  const { op, field, value } = leaf;

  if (op === "geo_within_km") {
    const v = value as RadiusValue;
    if (!v || typeof v.lat !== "number") return false;
    return haversineKm(point.lat, point.lng, v.lat, v.lng) <= v.km;
  }
  if (op === "geo_in_region") {
    const v = value as RegionValue;
    if (!v || !Array.isArray(v.bbox)) return false;
    const [minLng, minLat, maxLng, maxLat] = v.bbox;
    return point.lng >= minLng && point.lng <= maxLng && point.lat >= minLat && point.lat <= maxLat;
  }

  const cell = point.props[field] ?? "";
  switch (op) {
    case "eq":
      return cell === String(value ?? "");
    case "neq":
      return cell !== String(value ?? "");
    case "in":
      return Array.isArray(value) && (value as unknown[]).map(String).includes(cell);
    case "contains":
      return cell.toLowerCase().includes(String(value ?? "").toLowerCase());
    case "gt":
      return toNum(cell) > toNum(value as string | number);
    case "gte":
      return toNum(cell) >= toNum(value as string | number);
    case "lt":
      return toNum(cell) < toNum(value as string | number);
    case "lte":
      return toNum(cell) <= toNum(value as string | number);
    default:
      return false;
  }
}

/** Evaluate a filter tree against one point. */
export function matchesFilter(point: Point, node: FilterNode): boolean {
  if (isLeaf(node)) return matchLeaf(point, node);
  if ("all" in node) return node.all.every((n) => matchesFilter(point, n));
  if ("any" in node) return node.any.some((n) => matchesFilter(point, n));
  if ("not" in node) return !matchesFilter(point, node.not);
  return false;
}

/** Filter a point list to those matching the tree. */
export function selectPoints(points: Point[], node: FilterNode): Point[] {
  return points.filter((p) => matchesFilter(p, node));
}
